"use client"

import React, { useMemo, useCallback, useState } from 'react'
import NavigationBar from '@/widgets/navigation-bar/ui/navigation-bar'
import InputControls from '@/widgets/input-controls/ui/input-controls'
import { isLightBackground } from '@/widgets/input-controls/lib/is-light-background'
import { NavigationContext } from '@/layouts/avatar-page/model/use-navigate-chat-mode'
import ThreeCanvas from '@/widgets/three-canvas/ui/three-canvas'
import ChatOverlay from '@/widgets/chat-overlay/ui/chat-overlay'
import { KioskIndicator } from '@/layouts/kiosk-avatar-page/ui/kiosk-indicator'
import { ConsentModal } from '@/features/avatar/manage-consent/ui/consent-modal'
import { SearchStatusIndicator } from '@/features/search-chat'
import { useVadStore } from '@/shared/store/vad'
import { useAvatarStore } from '@/shared/store/avatar-store'
import { useCustomAvatarPage } from '../model/use-custom-avatar-page'
import { ToastContainer } from '../toast'
import { WebIdleTimer } from './web-idle-timer'
// ReconnectModal 제거됨 - 자동 재연결 처리
import type { AvatarPageConfig } from '../types/avatar-page-config'
import type { KioskIndicatorState } from '@/layouts/kiosk-avatar-page/types/kiosk-config'
import type ThreeJSApp from '@/shared/lib/threejs/app'
import { AvatarControlPanel } from '@/features/avatar/avatar-controls/ui/avatar-control-panel'
import { LipsyncTuningPanel } from '@/features/avatar/lipsync-tuning/ui/lipsync-tuning-panel'

interface CustomAvatarPageProps {
  agentId: string
  config?: Partial<AvatarPageConfig>
  isPreview?: boolean
  previewMode?: {
    hideConsentModal?: boolean
    forceMode?: 'default' | 'voice' | 'chat'
    showLoadingState?: boolean
  }
}

/**
 * Custom Avatar Page UI Component
 *
 * 책임:
 * - 레이아웃 구성
 * - 비즈니스 로직은 model/use-custom-avatar-page.ts에 위임
 * - 순수하게 props를 받아서 렌더링만 수행
 */
const CustomAvatarPage = ({
  agentId,
  config,
  isPreview = false,
  previewMode
}: CustomAvatarPageProps) => {
  // 모든 비즈니스 로직은 커스텀 훅에 위임
  const {
    mergedConfig,
    chatModeController,
    consentController,
    mediaController,
    messages,
    isLoading,
    pitch,
    pauseAudio,
    handleSendMessage,
    handleChatModeToggle,
    handleNavigateToMode,
    agentData,
    isAgentLoading,
    avatarType,
    isCustomAvatarLoaded,
    customAvatar, // 🔧 ThreeCanvas key에 사용
    // 자동 재연결 처리로 ReconnectModal 관련 props 제거됨
    // Search Chat (Backend Realtime API)
    isSearchEnabled,
    searchState,
    searchQuery,
    handleSearchToggle,
    showSearchToggle,
    // Idle Timer
    isTimerVisible,
    remainingSeconds,
    resetIdleTimer,
  } = useCustomAvatarPage({ agentId, config, enableIdleTimer: true })

  const [threeApp, setThreeApp] = useState<ThreeJSApp | null>(null)
  const handleThreeReady = useCallback((app: ThreeJSApp) => setThreeApp(app), [])

  // KioskIndicator용 상태 계산
  const vadStatus = useVadStore(state => state.vadStatus)
  const currentAnimation = useAvatarStore(state => state.currentAnimation)

  const indicatorState: KioskIndicatorState = useMemo(() => {
    if (consentController.isConsentModalVisible) return 'idle'
    if (currentAnimation === 'speaking') return 'speaking'
    if (isLoading) return 'loading'
    if (vadStatus === 'listening') return 'listening'
    return 'idle'
  }, [consentController.isConsentModalVisible, currentAnimation, isLoading, vadStatus])

  // Speaking 상태에서 터치 시 오디오 정지 + listening 상태로 전환 (KioskAvatarPage와 동일)
  const handleIndicatorTouch = useCallback(() => {
    pauseAudio()
    useVadStore.getState().setVadStatus('listening')
    useAvatarStore.getState().setCurrentAnimation('listening')
  }, [pauseAudio])

  // 배경 스타일 계산 - 모든 Hook은 조건부 return 전에 호출해야 함
  const backgroundStyle = useMemo(() => {
    const overlay = mergedConfig.chatOverlay

    if (overlay?.backgroundType === 'image' && overlay?.backgroundImageUrl) {
      return {
        backgroundImage: `url(${overlay.backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      } as React.CSSProperties
    }

    return {
      background: overlay?.background || '#FFFFFF',
    } as React.CSSProperties
  }, [mergedConfig.chatOverlay])

  // 채팅 오버레이 배경 밝기에 따라 입력바 스타일 결정 (밝은 배경 → 어두운 글자)
  const inputVariant = useMemo(
    () => (isLightBackground(mergedConfig.chatOverlay?.background || '#FFFFFF') ? 'light' : 'dark') as 'light' | 'dark',
    [mergedConfig.chatOverlay]
  )

  // Loading state - 커스텀 아바타인 경우 데이터 로드 완료까지 대기
  const isPageLoading = isAgentLoading || !agentData || (avatarType === 'custom' && !isCustomAvatarLoaded)

  if (isPageLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    )
  }

  // Preview mode logic
  const isConsentModalVisible = previewMode?.hideConsentModal
    ? false
    : consentController.isConsentModalVisible

  // Force chat mode if specified in preview mode
  const currentChatMode = previewMode?.forceMode === 'chat'
    ? 'chat'
    : previewMode?.forceMode === 'voice'
      ? 'voice'
      : chatModeController.currentMode

  // Navigation context
  const navigationContextValue = {
    navigateToMode: handleNavigateToMode
  }

  return (
    <NavigationContext.Provider value={navigationContextValue}>
      <main
        className='w-full h-full relative'
        style={backgroundStyle}
      >
        {/* ===== 전체화면 요소 (컨테이너 밖) ===== */}

        {/* Consent Modal - 전체화면 동의 모달 (fixed z-200) */}
        <ConsentModal
          isOpen={isConsentModalVisible}
          onConfirm={consentController.grantConsent}
          onDecline={consentController.declineConsent}
          config={{
            title: mergedConfig.consentModal?.title || agentData?.name,
            description: mergedConfig.consentModal?.description,
            confirmButtonText: mergedConfig.consentModal?.buttonText,
            background: mergedConfig.consentModal?.background,
            image: mergedConfig.consentModal?.image,
            videoUrl: mergedConfig.consentModal?.videoUrl,
          }}
          isPreview={isPreview}
          isInitializing={consentController.isInitializing}
          isAvatarLoading={consentController.isAvatarLoading}
          isGreetingLoading={consentController.isGreetingLoading}
        />

        {/* Avatar Canvas - 전체화면 3D 배경 (absolute z-1) */}
        <ThreeCanvas
          key={`three-canvas-${agentId}-${customAvatar?.id || 'default'}`}
          className='z-[1] absolute inset-0 !h-full !w-full'
          avatarType={avatarType}
          onReady={handleThreeReady}
        />

        {/* 아바타 크기/조명 조절 패널 (개발 환경 전용) */}
        {process.env.NODE_ENV === 'development' && <AvatarControlPanel app={threeApp} />}

        {/* 립싱크 튜닝 패널 (?tuning=1 로 표시) */}
        <LipsyncTuningPanel />

        {/* Chat Overlay - 전체화면 채팅 배경 (fixed z-9) */}
        <ChatOverlay
          messages={messages}
          isLoading={isLoading}
          isVisible={currentChatMode === 'chat'}
          background={mergedConfig.chatOverlay?.background || '#FFFFFF'}
          isPreview={isPreview}
          profileImageUrl={mergedConfig.chatOverlay?.styleConfig?.aiProfileImage}
        />

        {/* Nav gradient - 전체 너비 (컨테이너 밖) */}
        {!isConsentModalVisible && (
          <div
            className="absolute top-0 left-0 right-0 z-[5]"
            style={{
              height: '82px',
              background: 'linear-gradient(180deg, rgba(18, 19, 20, 0.5) 0%, rgba(18, 19, 20, 0) 100%)',
              opacity: 0.7,
            }}
          />
        )}

        {/* Navigation Bar - 컨테이너 밖으로 이동 (pointer-events 문제 해결) */}
        {!isConsentModalVisible && (
          <div
            className="absolute top-0 left-0 right-0 z-[100] pointer-events-auto"
            style={{
              width: 'min(100%, calc(100vh * 9 / 16))',
              margin: '0 auto',
              containerType: 'size',
            }}
          >
            <NavigationBar
              title={agentData?.name}
              variant={currentChatMode === 'chat' ? inputVariant : 'dark'}
            />
          </div>
        )}

        {/* ===== 9:16 반응형 컨테이너 (UI 요소) ===== */}
        <div
          className="relative z-[10] h-full mx-auto overflow-hidden pointer-events-none"
          style={{
            width: 'min(100%, calc(100vh * 9 / 16))',
            containerType: 'size',
          }}
        >

          {/* Kiosk Indicator (bottom-[15%] = 컨테이너 높이의 15%) */}
          {currentChatMode === 'voice' && !isConsentModalVisible && indicatorState !== 'idle' && (
            <KioskIndicator
              state={indicatorState}
              onScreenTouch={handleIndicatorTouch}
              pitch={pitch}
              selectedLanguage="ko"
              positionClass="bottom-[88px]"
              size="mobile"
            />
          )}

          {/* Search Status Indicator */}
          {searchState !== 'idle' && currentChatMode === 'chat' && (
            <SearchStatusIndicator />
          )}

          {/* Input Controls */}
          <InputControls
            chatMode={currentChatMode}
            onChatModeToggle={handleChatModeToggle}
            onSendMessage={handleSendMessage}
            isSpeakerOn={mediaController.isSpeakerOn}
            isMicOn={mediaController.isMicOn}
            onToggleSpeaker={mediaController.toggleSpeaker}
            onToggleMic={mediaController.toggleMic}
            blurStrength={mergedConfig.inputControls?.blurStrength || 25}
            placeholder={mergedConfig.inputControls?.placeholder}
            voiceButtonText={mergedConfig.inputControls?.voiceButtonText}
            chatButtonText={mergedConfig.inputControls?.chatButtonText}
            showSearchToggle={false}
            isSearchEnabled={isSearchEnabled}
            onSearchToggle={handleSearchToggle}
            variant={inputVariant}
          />

          {/* Toast Container */}
          <ToastContainer />
        </div>

        {/* 유휴 타이머 비활성화 */}
      </main>
    </NavigationContext.Provider>
  )
}

export default CustomAvatarPage
